/**
 * Airtable-style multi-condition filter panel.
 *
 * Mirrors the Monday "vibe" app's filter UX:
 *   • Top "Filter" button shows the active-condition count.
 *   • Click → popover opens with a vertical list of conditions, each
 *     row = [Field] [Operator] [Value] [X].
 *   • Add / remove / edit conditions; "Clear all" resets.
 *   • Conditions are combined with AND.
 *
 * All filtering is done CLIENT-SIDE against the rows already loaded
 * (≤500 cases — small enough to be instant). The parent passes:
 *    - `fields`     metadata array: { key, label, type, options? }
 *    - `value`      current conditions array (lifted state)
 *    - `onChange`   setter for conditions
 *
 * Then the parent calls `applyAirtableFilters(rows, conditions, fields)`
 * to derive the visible rows.
 */
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter as FilterIcon, X, Plus, ChevronDown } from "lucide-react";

// Operators by field type — kept short on purpose so the UI doesn't sprawl
const OPS = {
  text: [
    { key: "contains",      label: "contains" },
    { key: "not_contains",  label: "doesn't contain" },
    { key: "starts_with",   label: "starts with" },     // multi-letter A-Z picker
    { key: "eq",            label: "is" },
    { key: "neq",           label: "is not" },
    { key: "empty",         label: "is empty",    noValue: true },
    { key: "not_empty",     label: "is not empty", noValue: true },
  ],
  number: [
    { key: "eq",   label: "=" },
    { key: "neq",  label: "≠" },
    { key: "gt",   label: ">" },
    { key: "gte",  label: "≥" },
    { key: "lt",   label: "<" },
    { key: "lte",  label: "≤" },
    { key: "between", label: "between", twoValues: true },
  ],
  select: [
    { key: "is",     label: "is" },
    { key: "is_not", label: "is not" },
  ],
  date: [
    { key: "on",       label: "on" },
    { key: "from",     label: "on or after (from)" },
    { key: "to",       label: "on or before (to)" },
    { key: "between",  label: "between", twoValues: true },
    { key: "before",   label: "before" },
    { key: "after",    label: "after" },
    { key: "last_n",   label: "in last N days", numericValue: true },
    { key: "empty",    label: "is empty",     noValue: true },
    { key: "not_empty", label: "is not empty", noValue: true },
  ],
};

const opMeta = (fieldType, opKey) =>
  (OPS[fieldType] || []).find(o => o.key === opKey) || (OPS[fieldType] || [])[0];

// ── Predicate compiler ────────────────────────────────────────────────
export function applyAirtableFilters(rows, conditions, fields) {
  if (!conditions || conditions.length === 0) return rows;
  const byKey = Object.fromEntries(fields.map(f => [f.key, f]));
  return rows.filter(row => conditions.every(c => {
    const f = byKey[c.field];
    if (!f) return true;
    // A half-built condition (value not typed yet) must not hide everything
    const m = opMeta(f.type, c.op);
    if (m && !m.noValue && (c.value === "" || c.value == null)) return true;
    if (m?.twoValues && (c.value2 === "" || c.value2 == null)) return true;
    const raw = row[f.key];
    const v = raw == null ? "" : raw;
    const t = f.type;
    const op = c.op;
    if (t === "text" || t === "select") {
      const s = String(v).toLowerCase();
      // "__empty__" is a sentinel for blank values (Radix Select forbids
      // empty-string item values — using "" crashes the whole page)
      const rawTarget = c.value === "__empty__" ? "" : (c.value || "");
      const target = rawTarget.toString().toLowerCase();
      switch (op) {
        case "contains":     return s.includes(target);
        case "not_contains": return !s.includes(target);
        case "eq": case "is":         return s === target;
        case "neq": case "is_not":    return s !== target;
        case "empty":        return !s;
        case "not_empty":    return !!s;
        case "starts_with": {
          // value is a comma-separated list of letters or strings (multi-pick).
          // "#" is a special token meaning "starts with a non-letter".
          const parts = target.split(",").map(p => p.trim()).filter(Boolean);
          if (parts.length === 0) return true;
          const firstChar = s.trim().charAt(0);
          const isLetterStart = firstChar >= "a" && firstChar <= "z";
          return parts.some(p => {
            if (p === "#") return !isLetterStart;
            return s.startsWith(p);
          });
        }
        default: return true;
      }
    }
    if (t === "number") {
      const n = Number(v);
      if (!Number.isFinite(n) && op !== "empty") return false;
      const a = Number(c.value);
      const b = Number(c.value2);
      switch (op) {
        case "eq":      return n === a;
        case "neq":     return n !== a;
        case "gt":      return n >  a;
        case "gte":     return n >= a;
        case "lt":      return n <  a;
        case "lte":     return n <= a;
        case "between": return n >= Math.min(a, b) && n <= Math.max(a, b);
        default: return true;
      }
    }
    if (t === "date") {
      // Compare as ISO strings — our case fields are stored as YYYY-MM-DD.
      const d = (v || "").toString().slice(0, 10);
      const target = (c.value || "").toString().slice(0, 10);
      switch (op) {
        case "before":  return d && d < target;
        case "after":   return d && d > target;
        case "on":      return d === target;
        case "from":    return d && d >= target;
        case "to":      return d && d <= target;
        case "between": {
          const t2 = (c.value2 || "").toString().slice(0, 10);
          if (!d || !target || !t2) return false;
          const lo = target < t2 ? target : t2, hi = target < t2 ? t2 : target;
          return d >= lo && d <= hi;
        }
        case "empty":     return !d;
        case "not_empty": return !!d;
        case "last_n": {
          const n = Number(c.value);
          if (!d || !Number.isFinite(n)) return false;
          const cutoff = new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
          return d >= cutoff;
        }
        default: return true;
      }
    }
    return true;
  }));
}

// ── UI ────────────────────────────────────────────────────────────────
let _id = 0;
const nextId = () => `f${++_id}_${Date.now().toString(36).slice(-4)}`;

export function AirtableFilter({ fields, value, onChange }) {
  const [open, setOpen] = useState(false);
  const count = value?.length || 0;

  const fieldsByKey = useMemo(
    () => Object.fromEntries(fields.map(f => [f.key, f])),
    [fields],
  );

  const addCondition = () => {
    const first = fields[0];
    const ops = OPS[first.type] || [];
    onChange([
      ...value,
      { id: nextId(), field: first.key, op: ops[0].key, value: "", value2: "" },
    ]);
  };

  const updateCondition = (id, patch) =>
    onChange(value.map(c => (c.id === id ? { ...c, ...patch } : c)));

  const removeCondition = (id) => onChange(value.filter(c => c.id !== id));

  const clearAll = () => onChange([]);

  // When a field changes, reset op to the first valid one for the new field type
  const handleFieldChange = (id, newFieldKey) => {
    const f = fieldsByKey[newFieldKey];
    if (!f) return;
    const firstOp = (OPS[f.type] || [])[0]?.key || "eq";
    updateCondition(id, { field: newFieldKey, op: firstOp, value: "", value2: "" });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={count > 0 ? "default" : "outline"}
          size="sm"
          className="h-9 relative"
          data-testid="ageing-filter-btn"
        >
          <FilterIcon className="w-3.5 h-3.5 mr-1.5" />
          Filter
          {count > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/90 text-indigo-700 text-[10px] font-bold tabular-nums">
              {count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[640px] p-0" align="start" data-testid="ageing-filter-popover">
        <div className="px-4 py-3 border-b bg-slate-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">
              {count === 0 ? "No filters applied" : `${count} filter${count === 1 ? "" : "s"} applied`}
            </span>
            {count > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs" data-testid="ageing-filter-clear-all">
                <X className="w-3 h-3 mr-1" /> Clear all
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">All conditions combined with AND.</p>
        </div>
        <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {value.length === 0 && (
            <div className="text-center py-6 text-sm text-muted-foreground italic">
              Add a condition to start filtering
            </div>
          )}
          {value.map((c, idx) => (
            <ConditionRow
              key={c.id}
              index={idx}
              condition={c}
              fields={fields}
              fieldsByKey={fieldsByKey}
              onChangeField={(k) => handleFieldChange(c.id, k)}
              onUpdate={(patch) => updateCondition(c.id, patch)}
              onRemove={() => removeCondition(c.id)}
            />
          ))}
        </div>
        <div className="px-4 py-3 border-t flex items-center justify-between bg-slate-50">
          <Button variant="ghost" size="sm" onClick={addCondition} className="h-8 text-xs" data-testid="ageing-filter-add-condition">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add condition
          </Button>
          <Button variant="default" size="sm" onClick={() => setOpen(false)} className="h-8 text-xs">
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── One condition row ─────────────────────────────────────────────────
function ConditionRow({ index, condition, fields, fieldsByKey, onChangeField, onUpdate, onRemove }) {
  const f = fieldsByKey[condition.field] || fields[0];
  const ops = OPS[f.type] || [];
  const meta = opMeta(f.type, condition.op) || ops[0];

  return (
    <div className="flex items-center gap-1.5" data-testid={`ageing-filter-row-${index}`}>
      <span className="w-12 text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
        {index === 0 ? "Where" : "And"}
      </span>
      {/* Field selector */}
      <Select value={condition.field} onValueChange={onChangeField}>
        <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`ageing-filter-field-${index}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {fields.map(ff => (
            <SelectItem key={ff.key} value={ff.key} className="text-xs">{ff.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Operator selector */}
      <Select value={condition.op} onValueChange={(op) => onUpdate({ op, value: "", value2: "" })}>
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`ageing-filter-op-${index}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ops.map(o => (
            <SelectItem key={o.key} value={o.key} className="text-xs">{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Value input — adapts to field type + operator */}
      <div className="flex-1 flex items-center gap-1.5">
        {meta?.noValue ? (
          <span className="text-xs text-muted-foreground italic">—</span>
        ) : condition.op === "starts_with" ? (
          <AlphabetMultiSelect
            value={condition.value || ""}
            onChange={(v) => onUpdate({ value: v })}
            testid={`ageing-filter-value-${index}`}
          />
        ) : f.type === "select" && f.options ? (
          <Select value={String(condition.value || "")} onValueChange={(v) => onUpdate({ value: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid={`ageing-filter-value-${index}`}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {f.options.filter(opt => String(opt.value) !== "").map(opt => (
                <SelectItem key={String(opt.value)} value={String(opt.value)} className="text-xs">{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <>
            <Input
              type={f.type === "number" || meta?.numericValue ? "number" : f.type === "date" ? "date" : "text"}
              value={condition.value || ""}
              onChange={(e) => onUpdate({ value: e.target.value })}
              className="h-8 text-xs"
              placeholder={f.type === "number" ? "0" : f.type === "date" ? "" : "Enter value"}
              data-testid={`ageing-filter-value-${index}`}
            />
            {meta?.twoValues && (
              <>
                <span className="text-[10px] text-muted-foreground">and</span>
                <Input
                  type={f.type === "date" ? "date" : "number"}
                  value={condition.value2 || ""}
                  onChange={(e) => onUpdate({ value2: e.target.value })}
                  className="h-8 text-xs"
                  placeholder="0"
                  data-testid={`ageing-filter-value2-${index}`}
                />
              </>
            )}
          </>
        )}
      </div>
      <Button variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 shrink-0" data-testid={`ageing-filter-remove-${index}`}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ── Alphabet multi-select — used by the "starts with" operator ────────────
// Renders as a compact popover button "Pick letters (3)" → grid of A-Z
// checkboxes with quick A-H / I-P / Q-Z range chips and a # chip for
// non-letter starts. Stored as comma-separated string so the existing
// condition shape (string value) stays untouched.
const _ALPHA = ["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"];

function AlphabetMultiSelect({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => {
    return new Set(
      (value || "")
        .split(",")
        .map(s => s.trim().toUpperCase())
        .filter(Boolean),
    );
  }, [value]);

  const emit = (next) => {
    const arr = [..._ALPHA, "#"].filter(L => next.has(L));
    onChange(arr.join(","));
  };

  const toggle = (L) => {
    const next = new Set(selected);
    if (next.has(L)) next.delete(L);
    else next.add(L);
    emit(next);
  };

  const setRange = (lo, hi) => {
    const next = new Set(selected);
    const range = _ALPHA.slice(_ALPHA.indexOf(lo), _ALPHA.indexOf(hi) + 1);
    const allInRange = range.every(L => next.has(L));
    for (const L of range) {
      if (allInRange) next.delete(L); else next.add(L);
    }
    emit(next);
  };

  const clearAll = () => onChange("");
  const selectAll = () => onChange([..._ALPHA].join(","));

  const summary = selected.size === 0
    ? "Pick letters…"
    : selected.size <= 4
      ? [..._ALPHA, "#"].filter(L => selected.has(L)).join(", ")
      : `${selected.size} letters`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-full text-xs justify-between font-normal"
          data-testid={testid}
        >
          <span className={selected.size === 0 ? "text-muted-foreground italic" : "text-slate-800"}>{summary}</span>
          <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-3" align="start" data-testid={`${testid}-popover`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Starts with letter</span>
          <div className="flex items-center gap-2 text-[10px]">
            <button onClick={selectAll} className="text-slate-700 hover:text-indigo-700 underline-offset-2 hover:underline" data-testid={`${testid}-all`}>All</button>
            <button onClick={clearAll} className="text-slate-700 hover:text-rose-700 underline-offset-2 hover:underline" data-testid={`${testid}-clear`}>Clear</button>
          </div>
        </div>
        {/* Range quick-chips */}
        <div className="flex items-center gap-1 mb-2">
          {[["A","H"], ["I","P"], ["Q","Z"]].map(([lo, hi]) => {
            const range = _ALPHA.slice(_ALPHA.indexOf(lo), _ALPHA.indexOf(hi) + 1);
            const allOn = range.every(L => selected.has(L));
            return (
              <button
                key={`${lo}-${hi}`}
                onClick={() => setRange(lo, hi)}
                className={`px-1.5 h-6 rounded text-[10px] font-semibold border transition-colors ${
                  allOn ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                }`}
                data-testid={`${testid}-range-${lo}-${hi}`}
              >
                {lo}–{hi}
              </button>
            );
          })}
        </div>
        {/* A-Z grid */}
        <div className="grid grid-cols-7 gap-1">
          {_ALPHA.map(L => {
            const on = selected.has(L);
            return (
              <button
                key={L}
                onClick={() => toggle(L)}
                className={`h-7 rounded text-[11px] font-semibold border transition-colors inline-flex items-center justify-center gap-1 ${
                  on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                }`}
                data-testid={`${testid}-letter-${L}`}
              >
                <Checkbox
                  checked={on}
                  className="pointer-events-none h-3 w-3"
                  aria-hidden="true"
                />
                {L}
              </button>
            );
          })}
          {/* '#' for non-letter starts (numbers, symbols) */}
          <button
            onClick={() => toggle("#")}
            className={`h-7 rounded text-[11px] font-semibold border transition-colors col-span-7 ${
              selected.has("#") ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
            }`}
            data-testid={`${testid}-letter-hash`}
          >
            # (numbers / symbols)
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ── Chip strip — shown above the table when filters are active ────────
export function FilterChips({ conditions, fields, onRemove, onClear }) {
  if (!conditions?.length) return null;
  const byKey = Object.fromEntries(fields.map(f => [f.key, f]));
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="ageing-filter-chips">
      {conditions.map((c, idx) => {
        const f = byKey[c.field];
        if (!f) return null;
        const meta = opMeta(f.type, c.op);
        let valueDisplay;
        if (meta?.noValue) {
          valueDisplay = "";
        } else if (meta?.twoValues) {
          valueDisplay = `${c.value} – ${c.value2}`;
        } else if (c.op === "starts_with") {
          // Multi-letter picker — keep the chip compact: "A, B, C…"
          const parts = (c.value || "").split(",").map(s => s.trim()).filter(Boolean);
          valueDisplay = parts.length <= 5 ? parts.join(", ") : `${parts.slice(0, 4).join(", ")}… +${parts.length - 4}`;
        } else if (f.type === "select" && f.options) {
          valueDisplay = f.options.find(o => String(o.value) === String(c.value))?.label ?? c.value;
        } else {
          valueDisplay = c.value;
        }
        return (
          <span
            key={c.id}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 border border-indigo-200 text-indigo-900"
            data-testid={`ageing-filter-chip-${idx}`}
          >
            <span className="font-medium">{f.label}</span>
            <span className="text-indigo-700/70">{meta?.label}</span>
            {valueDisplay && <span className="font-semibold">&ldquo;{valueDisplay}&rdquo;</span>}
            <button onClick={() => onRemove(c.id)} className="ml-0.5 hover:text-rose-700" aria-label="Remove filter">
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      <button
        onClick={onClear}
        className="text-[11px] text-muted-foreground hover:text-rose-700 underline underline-offset-2 px-1.5"
        data-testid="ageing-filter-chips-clear"
      >
        Clear all
      </button>
    </div>
  );
}
